/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useMinWidth } from '../../hookServices/useMinWidth';
import { useScreenWakeLock } from '../../hookServices/useScreenWakeLock';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { SEO_ENABLED } from '../../utils/seo';
import { B2B_SECTIONS_FORWARD_TO_OPEN_EMS_MARKETPLACE, openEmsMarketplaceUrl } from '../../utils/openEmsUrls';
import { Button, ScrollNumber } from '../../shared/ui';
import { ENV, HOST, PORT } from '../../environment';
import { submitB2bLead } from '../../hookServices/feedbackApi';
import { useGetB2bPublicStatsQuery } from '../../redux/api';
import Modal from '../../components/modal/Modal';
import {
  formatB2bLocationLine,
  B2B_DISTANCE_METER_OPTIONS,
  formatB2bDistanceMeters,
  formatB2bRegionRadiusKm,
  b2bLeadKind,
} from './b2bMessengerLinks';
import B2bTariffsTable from './B2bTariffsTable';
import B2bServicePriceList from './B2bServicePriceList';
import B2bAccordionSection from './B2bAccordionSection';
import B2bAccordionShareButton from './B2bAccordionShareButton';
import B2bBakeryCase from './B2bBakeryCase';
import B2bBennyCase from './B2bBennyCase';
import B2bSolarCase from './B2bSolarCase';
import DayKwhChart from './DayKwhChart';
import OreePriceIndicesWidget from './OreePriceIndicesWidget';
import OpenEmsMonthlyRatesWidget from './OpenEmsMonthlyRatesWidget';
import FlowGraph from './FlowGraph';
import B2bLocationMapPicker from './B2bLocationMapPicker';
import landingStyles from './B2bLanding.module.css';

const B2B_KW_OPTIONS = ['7+', '22+', '40+', '60+', '120+', '240+', '480+'];

/** Extra price per kWh the location owner wants (UAH). */
const B2B_PRICE_KWH_EXTRA_MIN = 0.5;
const B2B_PRICE_KWH_EXTRA_MAX = 5;
const B2B_PRICE_KWH_EXTRA_STEP = 0.1;
const B2B_PRICE_KWH_EXTRA_DEFAULT = 1;
/** Monthly rent per parking lot the location owner wants (UAH). */
const B2B_MONTHLY_PARKING_MIN = 0;
const B2B_MONTHLY_PARKING_MAX = 20000;
const B2B_MONTHLY_PARKING_STEP = 100;
const B2B_MONTHLY_PARKING_DEFAULT = 0;

/** Max total duration for count-up animation (ms) */
const MAX_ANIMATION_MS = 2000;
/** Only flip/animate the last N numbers; show the rest immediately */
const FLIP_LAST_N = 5;
/** Minimum delay (ms) for first step – max speed at load */
const MIN_STEP_DELAY_MS = 50;
/** Always add this to the displayed user count */
const DISPLAY_OFFSET = 2000;

/** НКРЕКП / NEURC news: EV charging at stations does not require electricity supply license (not B2B cabinet). */
const NKREKP_LICENSE_NEWS_URL =
  'https://www.nerc.gov.ua/news/zaryadka-elektromobiliv-na-elektrozaryadnikh-stantsiyakh-ne-vimagae-otrimannya-litsenzii-z-postachannya-elektrichnoi-energii-spozhivachu';

/** Format integer with thousands separator for readability (e.g. 6538 -> "6 538") */
const formatCount = value =>
  Number.isFinite(value) ? new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 }).format(value) : '';

const GAMMA_PUBLISHED_EN = 'https://220-kmcom-ns4u4bc.gamma.site/';
const GAMMA_EMBED_UA = 'https://gamma.app/embed/hdmykxuwvbu3zfg';
const GAMMA_EMBED_EN = GAMMA_PUBLISHED_EN;
const GAMMA_PRESENT_UA = 'https://gamma.app/docs/hdmykxuwvbu3zfg?mode=present';
const GAMMA_PRESENT_EN = GAMMA_PUBLISHED_EN;
const B2B_GAMMA_FONTS =
  'https://fonts.googleapis.com/css2?family=Fira+Sans:wght@400;500;600&family=Inconsolata:wght@700&display=swap';

export default function B2bLanding({ onEnterCabinet }) {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage || i18n.language || 'ua').toLowerCase().startsWith('en');
  const gammaEmbedSrc = isEnglish ? GAMMA_EMBED_EN : GAMMA_EMBED_UA;
  const gammaPresentUrl = isEnglish ? GAMMA_PRESENT_EN : GAMMA_PRESENT_UA;
  const [searchParams, setSearchParams] = useSearchParams();
  const stationParam = searchParams.get('station');
  const stationForPower = stationParam && stationParam !== 'null' ? stationParam : undefined;
  const isWideViewport = useMinWidth(992);
  const graphWideMode = searchParams.get('graphView') === '1' && isWideViewport;
  // Open a specific accordion section via URL, e.g. /b2b?section=marketplace
  const sectionParam = (searchParams.get('section') || '').toLowerCase();
  const shouldForwardToOpenEmsMarketplace = B2B_SECTIONS_FORWARD_TO_OPEN_EMS_MARKETPLACE.has(sectionParam);

  useEffect(() => {
    if (!shouldForwardToOpenEmsMarketplace) return;
    window.location.replace(openEmsMarketplaceUrl(searchParams));
  }, [shouldForwardToOpenEmsMarketplace, searchParams]);

  if (shouldForwardToOpenEmsMarketplace) {
    return null;
  }

  const openEmsMarketplaceHref = openEmsMarketplaceUrl(searchParams);

  const openGraphWide = () => {
    const next = new URLSearchParams(searchParams);
    next.set('graphView', '1');
    setSearchParams(next, { replace: false });
  };

  const closeGraphWide = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('graphView');
    setSearchParams(next, { replace: true });
  };

  /** LG webOS / TV: Wake Lock + hidden video loop while the wide kiosk is visible. */
  useScreenWakeLock(graphWideMode);

  const { data: stats } = useGetB2bPublicStatsQuery();
  const baseStart = stats != null ? Math.max(0, stats.evDriversCount - (stats.newUsersLast30Days ?? 0)) : 0;
  const baseEnd = stats?.evDriversCount ?? 0;
  const startCount = baseStart + DISPLAY_OFFSET;
  const endCount = baseEnd + DISPLAY_OFFSET;
  const [displayCount, setDisplayCount] = useState(() => startCount);
  const displayedValue = Math.max(displayCount, startCount);

  const [messengerModal, setMessengerModal] = useState({
    open: false,
    message: '',
    titleKey: 'b2bMessengerChoiceTitle',
    formType: null,
  });
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadKwAvailable, setLeadKwAvailable] = useState('');
  const [leadDistributionContract, setLeadDistributionContract] = useState('');
  const [leadLocations, setLeadLocations] = useState([]);
  const [leadDistanceMeters, setLeadDistanceMeters] = useState('');
  const [leadPriceKwhExtra, setLeadPriceKwhExtra] = useState(B2B_PRICE_KWH_EXTRA_DEFAULT);
  const [leadMonthlyParkingPrice, setLeadMonthlyParkingPrice] = useState(B2B_MONTHLY_PARKING_DEFAULT);
  const [leadFormErrors, setLeadFormErrors] = useState({});
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const leadNameInputRef = useRef(null);
  const leadPhoneInputRef = useRef(null);
  const leadSubmitLock = useRef(false);
  const isLocationLeadForm =
    messengerModal.formType === 'proposeLocation' || messengerModal.formType === 'lookingForLocation';
  const closeMessengerModal = () => {
    setLeadName('');
    setLeadPhone('');
    setLeadKwAvailable('');
    setLeadDistributionContract('');
    setLeadLocations([]);
    setLeadDistanceMeters('');
    setLeadPriceKwhExtra(B2B_PRICE_KWH_EXTRA_DEFAULT);
    setLeadMonthlyParkingPrice(B2B_MONTHLY_PARKING_DEFAULT);
    setLeadFormErrors({});
    setLeadSent(false);
    setMessengerModal({ open: false, message: '', titleKey: 'b2bMessengerChoiceTitle', formType: null });
  };
  const openB2bMessenger = (message, titleKey = 'b2bMessengerChoiceTitle', formType = null) => {
    setLeadSent(false);
    setMessengerModal({ open: true, message, titleKey, formType });
  };
  /** Read from DOM on submit so browser autofill is included (controlled state may lag until blur). */
  const buildLeadContactSuffix = () => {
    const n = (leadNameInputRef.current?.value ?? leadName).trim();
    const p = (leadPhoneInputRef.current?.value ?? leadPhone).trim();
    const lines = [];
    if (n) lines.push(`${t('b2bLeadFormNameLabel')}: ${n}`);
    if (p) lines.push(`${t('b2bLeadFormPhoneLabel')}: ${p}`);
    if (isLocationLeadForm) {
      if (leadKwAvailable) {
        lines.push(`${t('b2bLeadFormKwLabel')}: ${leadKwAvailable}`);
      }
      if (messengerModal.formType === 'proposeLocation' && leadDistributionContract) {
        const distributionValue = leadDistributionContract === 'yes' ? t('b2bLeadFormYes') : t('b2bLeadFormNo');
        lines.push(`${t('b2bLeadFormDistributionLabel')}: ${distributionValue}`);
      }
      if (leadLocations.length) {
        const locationsLabelKey =
          messengerModal.formType === 'lookingForLocation' ? 'b2bLeadFormRegionLabel' : 'b2bLeadFormLocationsLabel';
        const locationLines = leadLocations.map((loc, index) => {
          let line = formatB2bLocationLine(index, loc.label, loc.lat, loc.lng);
          if (loc.radius_km != null) {
            line += `\n${t('b2bLeadFormRegionRadiusLabel')}: ${formatB2bRegionRadiusKm(loc.radius_km, t)}`;
          }
          return line;
        });
        lines.push(`${t(locationsLabelKey)}:\n${locationLines.join('\n')}`);
      }
      if (leadDistanceMeters) {
        lines.push(`${t('b2bLeadFormDistanceLabel')}: ${formatB2bDistanceMeters(leadDistanceMeters, t)}`);
      }
      if (messengerModal.formType === 'proposeLocation') {
        lines.push(`${t('b2bLeadFormPriceKwhExtraLabel')}: ${leadPriceKwhExtra.toFixed(1)} ₴`);
        lines.push(`${t('b2bLeadFormMonthlyParkingLabel')}: ${formatCount(leadMonthlyParkingPrice)} ₴`);
      }
    }
    return lines.length ? `\n\n${lines.join('\n')}` : '';
  };
  const validateLeadForm = () => {
    const name = (leadNameInputRef.current?.value ?? leadName).trim();
    const phone = (leadPhoneInputRef.current?.value ?? leadPhone).trim();
    const errors = {};

    if (!name) errors.name = t('b2bLeadFormRequired');
    const phoneDigits = phone.replace(/\D/g, '');
    if (!phone) errors.phone = t('b2bLeadFormRequired');
    else if (phoneDigits.length < 9 || phoneDigits.length > 15) errors.phone = t('b2bLeadFormInvalidPhone');

    if (isLocationLeadForm) {
      if (!leadKwAvailable) errors.kw = t('b2bLeadFormRequired');
      if (messengerModal.formType === 'proposeLocation' && !leadDistributionContract) {
        errors.distribution = t('b2bLeadFormRequired');
      }
      if (!leadLocations.length) {
        errors.locations = t(
          messengerModal.formType === 'lookingForLocation'
            ? 'b2bLeadFormRegionRequired'
            : 'b2bLeadFormLocationsRequired'
        );
      }
    }

    return errors;
  };
  const buildFullLeadMessage = () => `${messengerModal.message || ''}${buildLeadContactSuffix()}`;
  const scrollToFirstLeadFormError = errors => {
    const order = ['name', 'phone', 'kw', 'distribution', 'locations'];
    const targetKey = order.find(key => errors[key]);
    const targetId =
      targetKey === 'name'
        ? 'b2b-lead-name'
        : targetKey === 'phone'
          ? 'b2b-lead-phone'
          : targetKey === 'kw'
            ? 'b2b-lead-kw'
            : targetKey === 'distribution'
              ? 'b2b-lead-distribution'
              : targetKey === 'locations'
                ? 'b2b-lead-locations'
                : null;
    if (!targetId) return;
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const submitLead = async () => {
    const { message, formType, titleKey } = messengerModal;
    if (!message || leadSubmitLock.current) return;

    const errors = validateLeadForm();
    if (Object.keys(errors).length) {
      setLeadFormErrors(errors);
      scrollToFirstLeadFormError(errors);
      return;
    }

    setLeadFormErrors({});
    const name = (leadNameInputRef.current?.value ?? leadName).trim();
    const phone = (leadPhoneInputRef.current?.value ?? leadPhone).trim();
    const fullMessage = buildFullLeadMessage();
    leadSubmitLock.current = true;
    setLeadSubmitting(true);
    try {
      await submitB2bLead({
        name,
        phone,
        message: fullMessage,
        channel: 'form',
        kind: b2bLeadKind(formType, titleKey),
      });
      setLeadSent(true);
    } catch (err) {
      if (err?.detail === 'INVALID_PHONE') {
        setLeadFormErrors({ phone: t('b2bLeadFormInvalidPhone') });
      } else {
        const key = err?.detail === 'RATE_LIMITED' ? 'b2bLeadFormRateLimit' : 'b2bLeadFormSaveError';
        setLeadFormErrors({ submit: t(key) });
      }
    } finally {
      leadSubmitLock.current = false;
      setLeadSubmitting(false);
    }
  };

  useEffect(() => {
    if (stats == null || endCount <= startCount) {
      setDisplayCount(endCount);
      return undefined;
    }
    const totalSteps = endCount - startCount;
    const stepsToAnimate = Math.min(totalSteps, FLIP_LAST_N);
    const animationStart = endCount - stepsToAnimate;
    setDisplayCount(animationStart);
    if (stepsToAnimate === 0) {
      return undefined;
    }
    // Delays increase over time: start at max speed, slow down by end of 3s
    // delay[i] = minDelay + i * slope, sum = MAX_ANIMATION_MS
    const n = stepsToAnimate;
    const sum = (n * (n - 1)) / 2; // 0+1+2+...+(n-1)
    const totalForLinear = n * MIN_STEP_DELAY_MS + sum; // if slope=1
    const slope = (MAX_ANIMATION_MS - n * MIN_STEP_DELAY_MS) / (sum || 1);
    const timeouts = [];
    let totalDelay = 0;
    for (let step = 0; step < stepsToAnimate; step += 1) {
      const delay = Math.round(MIN_STEP_DELAY_MS + step * slope);
      totalDelay += delay;
      const value = animationStart + step + 1;
      timeouts.push(window.setTimeout(() => setDisplayCount(value), totalDelay));
    }
    return () => timeouts.forEach(id => clearTimeout(id));
  }, [stats?.evDriversCount, stats?.newUsersLast30Days, startCount, endCount]);

  const MONTH_KEYS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthNameRaw = t('b2bMonth' + MONTH_KEYS[new Date().getMonth()]);
  const monthName = monthNameRaw.charAt(0).toUpperCase() + monthNameRaw.slice(1);

  const handleEnterCabinet = () => {
    const local = ENV === 'local' ? ':3000' : '';
    window.open(`${HOST}:${PORT}/oauth2/authorization/keycloak?redirect_uri=${HOST}${local}/oauth2/redirect`, '_blank');
  };

  if (graphWideMode) {
    return (
      <>
        <Helmet>
          <link rel="stylesheet" href={B2B_GAMMA_FONTS} />
          {SEO_ENABLED ? (
            <>
              <title>
                {t('b2bFlowGraphTitle')} | {t('b2bWelcomeTitle')} | 220-km.com
              </title>
              <meta name="description" content={t('b2bFlowGraphSeoMetaDescription')} />
              <meta name="keywords" content={t('b2bSeoMetaKeywords')} />
              <link rel="canonical" href="https://220-km.com/b2b" />
              <meta property="og:type" content="website" />
              <meta property="og:url" content="https://220-km.com/b2b" />
              <meta property="og:title" content={`${t('b2bFlowGraphTitle')} | ${t('b2bWelcomeTitle')} | 220-km.com`} />
              <meta property="og:description" content={t('b2bFlowGraphSeoMetaDescription')} />
              <meta property="og:image" content="https://220-km.com/logo512.png" />
              <meta property="og:site_name" content="220-km.com" />
              <meta name="robots" content="index, follow, max-image-preview:large" />
            </>
          ) : (
            <>
              <meta name="robots" content="noindex, nofollow" />
              <title>220 km</title>
            </>
          )}
        </Helmet>
        <div
          className={landingStyles.flowGraphWideViewport}
          role="dialog"
          aria-modal="true"
          aria-label={t('b2bFlowGraphTitle')}
        >
          <button type="button" className={landingStyles.flowGraphWideClose} onClick={closeGraphWide}>
            ← {t('b2bFlowGraphCloseWide')}
          </button>
          <div className={landingStyles.flowGraphWideCenter}>
            <FlowGraph station={stationForPower} wideView />
          </div>
          <img
            className={landingStyles.flowGraphWideQr}
            src={`${process.env.PUBLIC_URL || ''}/b2b-flow-graph-qr.png`}
            alt="220-km.com"
            width={244}
            height={244}
            decoding="async"
          />
        </div>
      </>
    );
  }

  const seoPageTitle = `${t('b2bWelcomeTitle')} | 220-km.com`;
  const seoDescription = t('b2bSeoMetaDescription');
  const seoKeywords = t('b2bSeoMetaKeywords');
  const seoCanonicalUrl = 'https://220-km.com/b2b';

  return (
    <>
      <Helmet>
        <link rel="stylesheet" href={B2B_GAMMA_FONTS} />
        {SEO_ENABLED ? (
          <>
            <title>{seoPageTitle}</title>
            <meta name="description" content={seoDescription} />
            <meta name="keywords" content={seoKeywords} />
            <link rel="canonical" href={seoCanonicalUrl} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={seoCanonicalUrl} />
            <meta property="og:title" content={seoPageTitle} />
            <meta property="og:description" content={seoDescription} />
            <meta property="og:image" content="https://220-km.com/logo512.png" />
            <meta property="og:site_name" content="220-km.com" />
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={seoCanonicalUrl} />
            <meta property="twitter:title" content={seoPageTitle} />
            <meta property="twitter:description" content={seoDescription} />
            <meta property="twitter:image" content="https://220-km.com/logo512.png" />
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'WebPage',
                '@id': 'https://220-km.com/b2b#webpage',
                url: 'https://220-km.com/b2b',
                name: t('b2bWelcomeTitle'),
                description: t('b2bSeoMetaDescription'),
                isPartOf: { '@id': 'https://220-km.com/#website' },
                about: {
                  '@type': 'SoftwareApplication',
                  name: 'Open EMS',
                  applicationCategory: 'EnergyManagementApplication',
                  operatingSystem: 'Docker',
                  url: 'https://github.com/220-organization/open-ems',
                  codeRepository: 'https://github.com/220-organization/open-ems',
                  license: 'https://www.gnu.org/licenses/gpl-3.0.html',
                  isAccessibleForFree: true,
                  publisher: { '@id': 'https://220-km.com/#organization' },
                },
              })}
            </script>
          </>
        ) : (
          <>
            <meta name="robots" content="noindex, nofollow" />
            <title>220 km</title>
          </>
        )}
      </Helmet>
      <Container fluid="lg" className={`d-flex flex-column align-items-center ${landingStyles.landingContainer}`}>
        <section className={landingStyles.addStationSection} aria-label="Add station">
          <div className={landingStyles.statsRowWithPower}>
            <div className={landingStyles.statsBlockReserved}>
              {stats != null && (
                <>
                  <div className={landingStyles.statsLabel}>{t('b2bEvDriversCountLabel')}</div>
                  <div className={landingStyles.statsNumberColumn}>
                    <span className={landingStyles.statsTotalCountWrap}>
                      <ScrollNumber
                        direction="up"
                        duration={0.32}
                        ease={[0.33, 0, 0.2, 1]}
                        className={landingStyles.statsTotalCountScroll}
                        numberStyle={{ fontSize: '42px', letterSpacing: '2px' }}
                        style={{
                          minWidth: '17ch',
                          height: '48px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {formatCount(displayedValue)}
                      </ScrollNumber>
                    </span>
                    <span className={landingStyles.statsBracket}>
                      {' ('}
                      <span className={landingStyles.statsNewCount}>+ {stats.newUsersLast30Days ?? 0}</span>
                      {t('b2bEvDriversCountInMonth', { month: monthName })}
                      {')'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <p className={landingStyles.addStationHint}>{t('b2bAddStationHint')}</p>
          <div className={landingStyles.wideButtonRow}>
            <Button
              variant="outline"
              onClick={() => openB2bMessenger(`${t('b2bTelegramConnectTo220Greeting')}\n\n#ПідключенняДо220`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                margin: '0 auto',
                width: '100%',
                minHeight: '56px',
                paddingLeft: 20,
                paddingRight: 20,
              }}
            >
              <span style={{ fontSize: '24px', fontWeight: '300', letterSpacing: '2px' }}>
                ⚡ {t('b2bConnectTo220Btn')}
              </span>
            </Button>
          </div>
        </section>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '16px',
            width: '100%',
          }}
        >
          <div className={landingStyles.wideButtonRow}>
            <Button
              onClick={onEnterCabinet || handleEnterCabinet}
              style={{
                borderRadius: '20px',
                minHeight: '56px',
                width: '100%',
                display: 'flex',
                gap: '12px',
                margin: '0 auto',
              }}
            >
              <span style={{ fontSize: '26px', letterSpacing: '4.3px' }}>{t('b2bEnterCabinetBtn')}</span>
            </Button>
          </div>
        </div>

        <B2bAccordionSection
          title={t('b2bAccordionTariffs')}
          className={landingStyles.accordionSection}
          defaultOpen={sectionParam === 'tariffs'}
        >
          <div className={landingStyles.tableSection}>
            <B2bAccordionShareButton section="tariffs" titleKey="b2bAccordionTariffs" textKey="b2bTariffsShareText" />
            <B2bTariffsTable hideTitle />
          </div>
        </B2bAccordionSection>

        <B2bAccordionSection
          title={t('b2bAccordionServiceRequest')}
          className={landingStyles.accordionSection}
          defaultOpen={sectionParam === 'service'}
        >
          <div className={landingStyles.tableSection}>
            <B2bAccordionShareButton
              section="service"
              titleKey="b2bAccordionServiceRequest"
              textKey="b2bServiceRequestShareText"
            />
            <B2bServicePriceList onRequest={message => openB2bMessenger(message, 'b2bAccordionServiceRequest')} />
          </div>
        </B2bAccordionSection>

        <B2bAccordionSection
          title={t('b2bAccordionLocationMarketplace')}
          className={landingStyles.accordionSection}
          headerHref={openEmsMarketplaceHref}
        />

        <B2bAccordionSection
          title={t('b2bAccordionDayAhead')}
          className={landingStyles.accordionSection}
          defaultOpen={sectionParam === 'dayahead'}
        >
          <div className={landingStyles.dayAheadSection}>
            <B2bAccordionShareButton
              section="dayahead"
              titleKey="b2bAccordionDayAhead"
              textKey="b2bDayAheadShareText"
            />
            <div className={landingStyles.flowGraphSection} style={{ marginTop: 0 }}>
              <div className={landingStyles.flowGraphWideActions}>
                <button type="button" className={landingStyles.flowGraphExpandBtn} onClick={openGraphWide}>
                  {t('b2bFlowGraphExpandWide')}
                </button>
              </div>
              <FlowGraph station={stationForPower} inPageSection />
            </div>
            <OpenEmsMonthlyRatesWidget />
            <DayKwhChart />
            <OreePriceIndicesWidget />
            <div className={`${landingStyles.wideButtonRow} ${landingStyles.consultRdnRow}`}>
              <Button
                variant="outline"
                onClick={() =>
                  openB2bMessenger(
                    `${t('b2bTelegramConsultRdnGreeting')}\n\n#ЗамовитиКонсультаціюПоРДН`,
                    'b2bConsultRdnBtn'
                  )
                }
                className={landingStyles.consultRdnBtn}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  margin: '0 auto',
                  width: '100%',
                  height: 'auto',
                  minHeight: '56px',
                  padding: '14px 20px',
                }}
              >
                <span className={landingStyles.consultRdnBtnLabel}>{t('b2bConsultRdnBtn')}</span>
              </Button>
            </div>
          </div>
        </B2bAccordionSection>

        <B2bAccordionSection
          title={t('b2bAccordionCaseBenny')}
          className={landingStyles.accordionSection}
          defaultOpen={sectionParam === 'case' || sectionParam === 'case-benny'}
        >
          <div className={landingStyles.dayAheadSection}>
            <B2bAccordionShareButton
              section="case-benny"
              titleKey="b2bAccordionCaseBenny"
              textKey="b2bCaseBennyShareText"
            />
            <B2bBennyCase />
          </div>
        </B2bAccordionSection>

        <B2bAccordionSection
          title={t('b2bAccordionCaseSolar')}
          className={landingStyles.accordionSection}
          defaultOpen={sectionParam === 'case' || sectionParam === 'case-solar'}
        >
          <div className={landingStyles.dayAheadSection}>
            <B2bAccordionShareButton
              section="case-solar"
              titleKey="b2bAccordionCaseSolar"
              textKey="b2bCaseSolarShareText"
            />
            <B2bSolarCase />
          </div>
        </B2bAccordionSection>

        <B2bAccordionSection
          title={t('b2bAccordionCaseBakery')}
          className={landingStyles.accordionSection}
          defaultOpen={sectionParam === 'case' || sectionParam === 'case-bakery'}
        >
          <div className={landingStyles.dayAheadSection}>
            <B2bAccordionShareButton
              section="case-bakery"
              titleKey="b2bAccordionCaseBakery"
              textKey="b2bCaseBakeryShareText"
            />
            <B2bBakeryCase />
          </div>
        </B2bAccordionSection>

        <a
          href={NKREKP_LICENSE_NEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={landingStyles.licenseLink}
        >
          {t('b2bLicenseLinkBtn')}
        </a>

        <Modal
          modalOpen={messengerModal.open}
          setModalOpen={nextOpen => {
            if (nextOpen === false) closeMessengerModal();
          }}
        >
          <div
            className={`${landingStyles.messengerChoiceBox}${isLocationLeadForm ? ` ${landingStyles.messengerChoiceBoxWide}` : ''}`}
            aria-labelledby="b2b-messenger-choice-title"
          >
            <p id="b2b-messenger-choice-title" className={landingStyles.messengerChoiceTitle}>
              {t(messengerModal.titleKey)}
            </p>
            {leadSent ? (
              <p className={landingStyles.leadFormSentStatus} role="status">
                {t('b2bLeadFormSentStatus')}
              </p>
            ) : (
              <>
                <div className={landingStyles.leadFormFields}>
                  <label className={landingStyles.leadFormLabel} htmlFor="b2b-lead-name">
                    {t('b2bLeadFormNameLabel')}
                    <span className={landingStyles.leadFormRequiredMark}>*</span>
                  </label>
                  <input
                    id="b2b-lead-name"
                    ref={leadNameInputRef}
                    className={`${landingStyles.leadFormInput}${leadFormErrors.name ? ` ${landingStyles.leadFormInputError}` : ''}`}
                    type="text"
                    name="b2bLeadName"
                    autoComplete="name"
                    required
                    value={leadName}
                    onChange={e => {
                      setLeadName(e.target.value);
                      if (leadFormErrors.name) {
                        setLeadFormErrors(prev => {
                          const next = { ...prev };
                          delete next.name;
                          return next;
                        });
                      }
                    }}
                    onInput={e => setLeadName(e.currentTarget.value)}
                    placeholder={t('b2bLeadFormNamePlaceholder')}
                  />
                  {leadFormErrors.name ? <p className={landingStyles.leadFormError}>{leadFormErrors.name}</p> : null}
                  <label className={landingStyles.leadFormLabel} htmlFor="b2b-lead-phone">
                    {t('b2bLeadFormPhoneLabel')}
                    <span className={landingStyles.leadFormRequiredMark}>*</span>
                  </label>
                  <input
                    id="b2b-lead-phone"
                    ref={leadPhoneInputRef}
                    className={`${landingStyles.leadFormInput}${leadFormErrors.phone ? ` ${landingStyles.leadFormInputError}` : ''}`}
                    type="tel"
                    name="b2bLeadPhone"
                    autoComplete="tel"
                    inputMode="tel"
                    required
                    value={leadPhone}
                    onChange={e => {
                      setLeadPhone(e.target.value);
                      if (leadFormErrors.phone) {
                        setLeadFormErrors(prev => {
                          const next = { ...prev };
                          delete next.phone;
                          return next;
                        });
                      }
                    }}
                    onInput={e => setLeadPhone(e.currentTarget.value)}
                    placeholder={t('b2bLeadFormPhonePlaceholder')}
                  />
                  {leadFormErrors.phone ? <p className={landingStyles.leadFormError}>{leadFormErrors.phone}</p> : null}
                  {isLocationLeadForm ? (
                    <>
                      <fieldset
                        id="b2b-lead-kw"
                        className={`${landingStyles.leadFormFieldset}${leadFormErrors.kw ? ` ${landingStyles.leadFormFieldsetError}` : ''}`}
                      >
                        <legend className={landingStyles.leadFormLabel}>
                          {t('b2bLeadFormKwLabel')}
                          <span className={landingStyles.leadFormRequiredMark}>*</span>
                        </legend>
                        <div
                          className={landingStyles.leadFormOptionRow}
                          role="radiogroup"
                          aria-label={t('b2bLeadFormKwLabel')}
                        >
                          {B2B_KW_OPTIONS.map(option => (
                            <button
                              key={option}
                              type="button"
                              role="radio"
                              aria-checked={leadKwAvailable === option}
                              className={`${landingStyles.leadFormOptionBtn}${leadKwAvailable === option ? ` ${landingStyles.leadFormOptionBtnActive}` : ''}`}
                              onClick={() => {
                                setLeadKwAvailable(option);
                                if (leadFormErrors.kw) {
                                  setLeadFormErrors(prev => {
                                    const next = { ...prev };
                                    delete next.kw;
                                    return next;
                                  });
                                }
                              }}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                        {leadFormErrors.kw ? <p className={landingStyles.leadFormError}>{leadFormErrors.kw}</p> : null}
                      </fieldset>
                      {messengerModal.formType === 'proposeLocation' ? (
                        <fieldset
                          id="b2b-lead-distribution"
                          className={`${landingStyles.leadFormFieldset}${leadFormErrors.distribution ? ` ${landingStyles.leadFormFieldsetError}` : ''}`}
                        >
                          <legend className={landingStyles.leadFormLabel}>
                            {t('b2bLeadFormDistributionLabel')}
                            <span className={landingStyles.leadFormRequiredMark}>*</span>
                          </legend>
                          <div
                            className={landingStyles.leadFormOptionRow}
                            role="radiogroup"
                            aria-label={t('b2bLeadFormDistributionLabel')}
                          >
                            {[
                              { value: 'yes', label: t('b2bLeadFormYes') },
                              { value: 'no', label: t('b2bLeadFormNo') },
                            ].map(option => (
                              <button
                                key={option.value}
                                type="button"
                                role="radio"
                                aria-checked={leadDistributionContract === option.value}
                                className={`${landingStyles.leadFormOptionBtn}${leadDistributionContract === option.value ? ` ${landingStyles.leadFormOptionBtnActive}` : ''}`}
                                onClick={() => {
                                  setLeadDistributionContract(option.value);
                                  if (leadFormErrors.distribution) {
                                    setLeadFormErrors(prev => {
                                      const next = { ...prev };
                                      delete next.distribution;
                                      return next;
                                    });
                                  }
                                }}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                          {leadFormErrors.distribution ? (
                            <p className={landingStyles.leadFormError}>{leadFormErrors.distribution}</p>
                          ) : null}
                        </fieldset>
                      ) : null}
                      <div
                        id="b2b-lead-locations"
                        className={`${landingStyles.leadFormFieldset}${leadFormErrors.locations ? ` ${landingStyles.leadFormFieldsetError}` : ''}`}
                      >
                        <span className={landingStyles.leadFormLabel}>
                          {t(
                            messengerModal.formType === 'lookingForLocation'
                              ? 'b2bLeadFormRegionLabel'
                              : 'b2bLeadFormLocationsLabel'
                          )}
                          <span className={landingStyles.leadFormRequiredMark}>*</span>
                        </span>
                        <B2bLocationMapPicker
                          selectionMode={messengerModal.formType === 'lookingForLocation' ? 'region' : 'point'}
                          locations={leadLocations}
                          onChange={nextLocations => {
                            setLeadLocations(nextLocations);
                            if (leadFormErrors.locations && nextLocations.length) {
                              setLeadFormErrors(prev => {
                                const next = { ...prev };
                                delete next.locations;
                                return next;
                              });
                            }
                          }}
                        />
                        {leadFormErrors.locations ? (
                          <p className={landingStyles.leadFormError}>{leadFormErrors.locations}</p>
                        ) : null}
                      </div>
                      {messengerModal.formType === 'proposeLocation' ? (
                        <>
                          <label className={landingStyles.leadFormLabel} htmlFor="b2b-lead-distance">
                            {t('b2bLeadFormDistanceLabel')}
                          </label>
                          <select
                            id="b2b-lead-distance"
                            className={landingStyles.leadFormInput}
                            value={leadDistanceMeters}
                            onChange={e => setLeadDistanceMeters(e.target.value)}
                          >
                            <option value="">{t('b2bLeadFormDistancePlaceholder')}</option>
                            {B2B_DISTANCE_METER_OPTIONS.map(option => (
                              <option key={option} value={String(option)}>
                                {formatB2bDistanceMeters(option, t)}
                              </option>
                            ))}
                          </select>
                          <div className={landingStyles.leadFormFieldset}>
                            <label className={landingStyles.leadFormSliderHeader} htmlFor="b2b-lead-price-kwh">
                              <span className={landingStyles.leadFormLabel}>{t('b2bLeadFormPriceKwhExtraLabel')}</span>
                              <span className={landingStyles.leadFormSliderValue}>
                                {leadPriceKwhExtra.toFixed(1)} ₴
                              </span>
                            </label>
                            <input
                              id="b2b-lead-price-kwh"
                              className={landingStyles.leadFormSlider}
                              type="range"
                              min={B2B_PRICE_KWH_EXTRA_MIN}
                              max={B2B_PRICE_KWH_EXTRA_MAX}
                              step={B2B_PRICE_KWH_EXTRA_STEP}
                              value={leadPriceKwhExtra}
                              onChange={e => setLeadPriceKwhExtra(Number.parseFloat(e.target.value))}
                            />
                            <div className={landingStyles.leadFormSliderScale}>
                              <span>{B2B_PRICE_KWH_EXTRA_MIN.toFixed(1)} ₴</span>
                              <span>{B2B_PRICE_KWH_EXTRA_MAX.toFixed(1)} ₴</span>
                            </div>
                          </div>
                          <div className={landingStyles.leadFormFieldset}>
                            <label className={landingStyles.leadFormSliderHeader} htmlFor="b2b-lead-monthly-parking">
                              <span className={landingStyles.leadFormLabel}>{t('b2bLeadFormMonthlyParkingLabel')}</span>
                              <span className={landingStyles.leadFormSliderValue}>
                                {formatCount(leadMonthlyParkingPrice)} ₴
                              </span>
                            </label>
                            <input
                              id="b2b-lead-monthly-parking"
                              className={landingStyles.leadFormSlider}
                              type="range"
                              min={B2B_MONTHLY_PARKING_MIN}
                              max={B2B_MONTHLY_PARKING_MAX}
                              step={B2B_MONTHLY_PARKING_STEP}
                              value={leadMonthlyParkingPrice}
                              onChange={e => setLeadMonthlyParkingPrice(Number.parseInt(e.target.value, 10))}
                            />
                            <div className={landingStyles.leadFormSliderScale}>
                              <span>{formatCount(B2B_MONTHLY_PARKING_MIN)} ₴</span>
                              <span>{formatCount(B2B_MONTHLY_PARKING_MAX)} ₴</span>
                            </div>
                          </div>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </div>
                <div className={landingStyles.leadFormMessagePreviewBox} aria-live="polite">
                  <p className={landingStyles.leadFormMessagePreviewLabel}>{t('b2bLeadFormMessagePreview')}</p>
                  <pre className={landingStyles.leadFormMessagePreviewText}>{buildFullLeadMessage()}</pre>
                </div>
                {leadFormErrors.submit ? (
                  <p className={landingStyles.leadFormSubmitError} role="alert">
                    {leadFormErrors.submit}
                  </p>
                ) : Object.keys(leadFormErrors).length ? (
                  <p className={landingStyles.leadFormSubmitError} role="alert">
                    {t('b2bLeadFormSubmitError')}
                  </p>
                ) : null}
                <Button className={landingStyles.leadFormSubmitBtn} disabled={leadSubmitting} onClick={submitLead}>
                  {t('b2bLeadFormSubmit')}
                </Button>
              </>
            )}
          </div>
        </Modal>
      </Container>

      <section className={landingStyles.gammaEmbedSection} aria-label="220-km.com presentation">
        <iframe
          key={gammaEmbedSrc}
          className={landingStyles.gammaEmbed}
          src={gammaEmbedSrc}
          title="220-km.com"
          allowFullScreen
        />
        <a href={gammaPresentUrl} target="_blank" rel="noopener noreferrer" className={landingStyles.gammaPresentLink}>
          {t('b2bGammaPresentationOpen')}
        </a>
      </section>
    </>
  );
}
